// @ts-strict-ignore
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { selectors } from 'store/selectors'
import { removeCompareItem } from 'store/slices/compare'
import { addToFavorites, removeFromFavorites } from 'store/slices/favorites'

import { useProductCurrentReviewSummary } from '@/features/product-reviews'

import { TProductExtended } from '@/entities/card'
import { ProductRating } from '@/entities/card/ui/product-rating/ProductRating'

import { addToCompare } from '@/shared/lib/utils/compare/addToCompare'
import { unEscape } from '@/shared/lib/utils/string/un-escape'
import { OrderType, TProductCode } from '@/shared/types/product-types'
import { Col, Row, ShareButton } from '@/shared/ui'
import { TagsList } from '@/shared/ui/tags'

import { useProduct } from '../../../hooks/useProduct'
import comparison from '../../assets/comparison.svg'
import iconLike from '../../assets/likeSmall.svg'

import { ProductToggleButton } from './product-toggle-button/product-toggle-button'

import cl from './product-page-header.module.scss'

export const ProductPageHeader = (): JSX.Element => {
	const dispatch = useDispatch()
	const data = useProduct()
	const product = data.product as TProductExtended
	const { summary: reviewsSummary, isLoading: isReviewsSummaryLoading } = useProductCurrentReviewSummary(
		product,
		data.crossLinks,
		data.isCrossLinksLoading
	)
	const [isInFavorites, setIsInFavorites] = useState<boolean>(false)
	const [isInCompare, setIsInCompare] = useState<boolean>(false)

	const favoriteProducts: TProductCode[] = useSelector(selectors.favorites)
	const inCompareProducts: TProductCode[] = useSelector(selectors.compareItemsCodes)
	const categories = useSelector(selectors.categoriesItems)

	// Updating internal state
	useEffect(() => {
		setIsInFavorites(favoriteProducts.includes(product.KOD))
		setIsInCompare(inCompareProducts.includes(product.KOD))
	}, [inCompareProducts, favoriteProducts, product.KOD])

	// Handlers
	const onFavoriteClick = (): void => {
		if (isInFavorites) {
			dispatch(removeFromFavorites(product.KOD))
		} else {
			dispatch(addToFavorites(product.KOD))
		}
	}

	const onCompareClick = (): void => {
		if (isInCompare) {
			dispatch(removeCompareItem({ productKOD: product.KOD }))
		} else {
			addToCompare(product.KOD, product.SECTION_ID, categories, dispatch)
		}
	}

	return (
		<section className={cl.productTop}>
			{!!product.PROMOTION.length && product.ORDER_TYPE !== OrderType.Archive && (
				<div className={cl.productTopPromotions}>
					<TagsList
						tags={product.PROMOTION}
						isNew={Array.isArray(product.IS_NEW) ? product.IS_NEW[0] === 'Да' : product.IS_NEW === 'Да'}
					/>
				</div>
			)}

			<div className={cl.productTopServiceRow}>
				<Row>
					<Col xl={17} lg={14} md={0} xs={0}>
						{/* <Text id="productTitle" className={cl.productTopTitle}>
							{unEscape(product.TITLE)}
						</Text> */}
						<h1 id="productTitle" className={cl.productTopTitle}>
							{unEscape(product.TITLE)}
						</h1>
					</Col>

					<Col xl={7} lg={10} md={24} xs={24}>
						<div className={cl.productTopMobileServiceLine}>
							<ProductRating
								className={cl.productTopMobileRating}
								summary={reviewsSummary}
								productCode={product.KOD}
								isMobileCompact={false}
								disableNavigation
								showAverage={false}
								showEmptyReviewsText={!isReviewsSummaryLoading}
								showEmptyReviewsStars
								underlineReviewsText
							/>

							<div className={cl.productTopProductButtonList}>
								<ProductToggleButton
									iconId={comparison}
									aria-pressed={isInCompare}
									onClick={onCompareClick}
									className={`gtm-product-compare`}>
									{isInCompare && `В сравнении`}
									{!isInCompare && `В сравнение`}
								</ProductToggleButton>

								<ProductToggleButton
									iconId={iconLike}
									aria-pressed={isInFavorites}
									onClick={onFavoriteClick}
									className={`gtm-product-favorites`}>
									{isInFavorites && `В избранном`}
									{!isInFavorites && `В избранное`}
								</ProductToggleButton>

								<ShareButton />
							</div>
						</div>
					</Col>
				</Row>
			</div>
		</section>
	)
}
